/**
 * @fileoverview Warning overlay for the Period Calculator.
 * Displays a warning when users navigate 6+ months ahead.
 * @module period-tracker/period-warning
 */

/* global femcare */

femcare.periodTracker = femcare.periodTracker || {};

/**
 * @namespace femcare.periodTracker.warning
 * @description Manages the prediction warning overlay display.
 */
femcare.periodTracker.warning = {
    isRevealed: false,
    elements: {
        warning: null,
        revealButton: null,
        circularView: null,
        calendarView: null
    },

    /**
     * Initializes the warning module.
     * @param {Object} config - Configuration object
     * @param {HTMLElement} config.circularView - Circular view element
     * @param {HTMLElement} config.calendarView - Calendar view element
     */
    init: function (config) {
        this.elements.circularView = config.circularView;
        this.elements.calendarView = config.calendarView;

        this.cacheElements();
        this.bindEvents();
    },

    /**
     * Caches warning DOM elements.
     */
    cacheElements: function () {
        this.elements.warning = document.querySelector('.period-warning');
        this.elements.revealButton = document.querySelector('.period-warning__action');
    },

    /**
     * Binds warning event listeners.
     */
    bindEvents: function () {
        if (this.elements.revealButton) {
            this.elements.revealButton.addEventListener('click', () => {
                this.isRevealed = true;
                this.dismissWarningOverlay();
            });
        }
    },

    /**
     * Calculates months difference from current date.
     * @param {Date} targetMonth - Target month to compare
     * @returns {number} Number of months ahead (positive) or behind (negative)
     */
    getMonthsFromCurrent: function (targetMonth) {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        const targetYear = targetMonth.getFullYear();
        const targetMonthIndex = targetMonth.getMonth();

        return (targetYear - currentYear) * 12 + (targetMonthIndex - currentMonth);
    },

    /**
     * Checks if prediction warning is required for given month.
     * @param {Date} month - Current navigation month
     * @returns {boolean} True if warning should be displayed
     */
    isPredictionWarningRequired: function (month) {
        if (this.isRevealed) {
            return false;
        }
        const monthsAhead = this.getMonthsFromCurrent(month);
        const threshold = femcare.periodTracker.config.defaults.warningThresholdMonths;
        return monthsAhead >= threshold;
    },

    /**
     * Displays warning overlay and temporarily hides calendar views.
     */
    displayWarningOverlay: function () {
        const navigation = femcare.periodTracker.navigation;

        if (this.elements.warning) {
            this.elements.warning.classList.remove('hidden');
        }

        if (this.elements.circularView) {
            this.elements.circularView.classList.add('hidden');
        }

        if (this.elements.calendarView) {
            this.elements.calendarView.classList.add('hidden');
        }

        if (navigation && navigation.nextMonthButton) {
            navigation.nextMonthButton.disabled = true;
            navigation.nextMonthButton.classList.add('disabled');
        }
    },

    /**
     * Dismisses warning overlay and restores calendar view.
     */
    dismissWarningOverlay: function () {
        const navigation = femcare.periodTracker.navigation;

        if (this.elements.warning) {
            this.elements.warning.classList.add('hidden');
        }

        if (navigation && navigation.currentView === 'circular') {
            if (this.elements.circularView) {
                this.elements.circularView.classList.remove('hidden');
            }
        } else {
            if (this.elements.calendarView) {
                this.elements.calendarView.classList.remove('hidden');
            }
        }

        if (navigation) {
            navigation.updateButtonStates();
        }
    },

    /**
     * Evaluates month navigation and toggles warning visibility.
     * @param {Date} month - Current navigation month
     */
    evaluateMonthNavigation: function (month) {
        if (this.isPredictionWarningRequired(month)) {
            this.displayWarningOverlay();
        } else if (this.isRevealed) {
            this.dismissWarningOverlay();
        }
    },

    /**
     * Replaces warning HTML content (for teen/adult variations).
     * @param {string} warningHtml - New warning HTML content
     */
    replaceWarningContent: function (warningHtml) {
        if (!this.elements.warning) {
            return;
        }

        this.elements.warning.outerHTML = warningHtml;

        // Re-cache elements and rebind events
        this.cacheElements();
        this.bindEvents();
    }
};
